import { Alert, Button } from '@mui/joy';
import { lowerFirst, sumBy } from 'lodash';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { type UseToggle } from '@/shared/hooks/UseToggle';

import { DivisionType } from '~/common/enums';

import { formatAsCurrency } from '~/common/utils/numberUtils';
import { validateIndividualDivision } from '../../../Invoices/hoa/toValidate/hooks/useInvoiceValidationFormikContext';
import { withDerivedValues } from './RelationDivisionLines/RelationDivisionLines.utils';

import { useDivisionValues } from './RelationDivisionLines/RelationDivisionLines.hooks';

import FormField from '@/shared/components/2.0/input/FormField';
import FormikAutocomplete from '@/shared/components/2.0/formik/FormikAutocomplete';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';
import NumberInput from '@/shared/components/2.0/input/NumberInput/NumberInput';
import RelationDivisionLines from './RelationDivisionLines/RelationDivisionLines';
import SidebarModal from '@/shared/components/2.0/layout/SidebarModal';

type DivisionValues = {
  divisionType: DivisionType | null;
  shares: {
    buildingRelationId: string | null;
    percentage: string | null;
    share: number;
    unitId: string;
  }[];
};

const divisionTypeValues = Object.values(DivisionType);

type Props<T> = {
  bookYearId: string | null;
  homeownerAssociationId: string | null;
  divisionTypeName: string;
  onSave?: (values: DivisionValues) => void;
  onTotalAmountChange?: (amount: number) => void;
  sharesName: string;
  toggle: UseToggle<T>;
  sidebar?: boolean;
  totalAmount: number;
  readOnly?: boolean;
};

const Modal = <T,>(props: {
  sidebar: boolean;
  children: React.ReactNode;
  toggle: UseToggle<T>;
  description: string;
  title: string;
}) => {
  const { sidebar, children, ...rest } = props;

  if (sidebar) {
    return (
      <SidebarModal hideBackdrop showClose {...rest}>
        {children}
      </SidebarModal>
    );
  }

  return <KpcModal {...rest}>{children}</KpcModal>;
};

const IndividualDivisionModal = <T,>({
  sharesName,
  divisionTypeName,
  sidebar = false,
  totalAmount,
  toggle,
  bookYearId,
  homeownerAssociationId,
  onSave,
  onTotalAmountChange,
  readOnly = false,
}: Props<T>) => {
  const { divisionType, divisions } = useDivisionValues({
    divisionTypeName,
    parentName: sharesName,
  });
  const { t } = useTranslation();

  const formik = useFormikContext();

  const amounts = (() => {
    const dividedAmount = sumBy(divisions, (share) => share.amount ?? 0);

    return {
      divided: dividedAmount,
      leftToDivide: totalAmount - dividedAmount,
      toDivide: totalAmount,
    };
  })();

  const errors = (() => {
    const validationResult = validateIndividualDivision({
      divisionKey: null,
      grossAmount: totalAmount,
      shares: divisions,
    });

    return validationResult.errors;
  })();

  // purely for a quick fix for miscBookingLines
  // can be removed again (along with onSave prop) if a non formik controlled version is created
  const handleSave = () => {
    const divisionValues = formik.values as DivisionValues;

    onSave?.(divisionValues);
    toggle.hide();
  };

  const canChangeTotalAmount = !!onTotalAmountChange;

  const handleTotalAmountChange = (amount: number | null) => {
    if (!canChangeTotalAmount) return;

    const nextTotalAmount = amount ?? 0;
    const recalculatedShares = withDerivedValues({
      divisionType,
      shares: divisions,
      totalAmount: nextTotalAmount,
    });

    formik.setFieldValue(sharesName, recalculatedShares);
    onTotalAmountChange(nextTotalAmount);
  };

  const divisionTypeOptions = divisionTypeValues.map((value) => ({
    label: t(`divisionType.options.${lowerFirst(value)}`),
    value,
  }));

  return (
    <Modal<T>
      description={t('_inboundHoaInvoice.actions.amountToDivide', {
        amountLeftToDivide: formatAsCurrency(amounts.leftToDivide),
        totalAmountToDivide: formatAsCurrency(amounts.toDivide),
      })}
      sidebar={sidebar}
      title={t('_inboundHoaInvoice.actions.applyIndividualDivision')}
      toggle={toggle}
    >
      <div>
        <div className="mb-4 flex gap-4">
          <FormField className="min-w-0 flex-1" label={t('totalAmount')}>
            <NumberInput
              disabled={readOnly || !canChangeTotalAmount}
              value={totalAmount}
              onValueChange={(values) => handleTotalAmountChange(values.asNumber)}
            />
          </FormField>
          <div className="min-w-0 flex-1">
            <FormikAutocomplete
              isRequired
              disabled={readOnly}
              label={t('divisionType.title')}
              name={divisionTypeName}
              options={divisionTypeOptions}
              placeholder={t('divisionType.placeholder')}
            />
          </div>
        </div>
        <RelationDivisionLines
          additionalActions={<Button onClick={handleSave}>Ok</Button>}
          parent={{
            bookYearId,
            divisionTypeName,
            homeownerAssociationId,
            name: sharesName,
            readOnly,
          }}
          totalAmount={totalAmount}
        >
          {errors?.length > 0 && (
            <Alert className="mb-2" color="warning">
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </RelationDivisionLines>
      </div>
    </Modal>
  );
};

export default IndividualDivisionModal;
